import React, { useContext, useState, useEffect, useRef } from "react";
import { Button, Alert, Text } from "@mantine/core";
import {
  createCaptureSdk,
  createCaptureUiWithInstance,
} from "@microblink/capture";
import "@microblink/capture/style.css";
import { uploadImageToS3 } from "../../../../helpers";
import { DataContext } from "../../../../context/DataContext";
import { IconExclamationCircle } from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import FirefoxCameraWarning from "../../../../components/FirefoxCameraWarning";

export default function Microblink({ onComplete, onStart, onUpload }) {
  const { settings, text } = useContext(DataContext);
  const [sdk, setSdk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );

  async function processAndUpload(data, width, height, suffix) {
    console.log(`📤 Microblink: Starting upload for ${suffix}`);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 1.0)
    );
    console.log(`☁️ Microblink: Uploading ${suffix} to S3...`);
    await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob);
    console.log(`✅ Microblink: Upload complete for ${suffix}`);
  }

  async function sendResultToApi(requestId, documentGroup) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/microblink/stats`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            request_id: requestId,
            document_group: documentGroup,
          }),
        }
      );
      const result = await response.json();
      if (result.result !== "success") {
        console.error("Failed to send result to API:", result);
      }
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      console.error("Error while sending result to API:", error);
    }
  }

  const captureComponentRef = useRef(null);
  // guard against multiple simultaneous capture sessions
  const isCapturingRef = useRef(false);

  useEffect(() => {
    // Preload the SDK
    async function preloadSdk() {
      if (showFirefoxPrompt) return;
      setIsLoading(true);
      try {
        // Determine which license key to use based on hostname
        let licenseKey;
        if (window.location.hostname === "vx-fe.dcams.app") {
          licenseKey = process.env.REACT_APP_MB_KEY_VX;
        } else if (window.location.hostname === "sweepmate-fe.dcams.app") {
          licenseKey = "sRwCABZzd2VlcG1hdGUtZmUuZGNhbXMuYXBwBmxleUpEY21WaGRHVmtUMjRpT2pFM05qQTBNRE0zTURjM05UZ3NJa055WldGMFpXUkdiM0lpT2lJeVl6aG1OekJqTXkwME9UQm1MV1V3TVRRdE5HSmlOUzB5T1dWak1tWm1OVGRoTlRnaWZRPT3nraomcTzwLtbVcVB/bd3Rh3D6+0fIV7tBoGKjqaPuuxGBWeWKh2ixnQGmf/cUL4pOZv6BsstWVe5CmpYZtKM2di7r6P6KpeLNZEv8xC8c7d7k/jq8auGjryMX3V4=";
        } else if (window.location.hostname === "edge-aug-21.fe-dcams.pages.dev") {
          // Hardcoded key specifically for edge-aug-21.fe-dcams.pages.dev domain
          licenseKey = "sRwCAB5lZGdlLWF1Zy0yMS5mZS1kY2Ftcy5wYWdlcy5kZXYGbGV5SkRjbVZoZEdWa1QyNGlPakUzTlRZNE5EVTVOamc0TkRjc0lrTnlaV0YwWldSR2IzSWlPaUl5WXpobU56QmpNeTAwT1RCbUxXVXdNVFF0TkdKaU5TMHlPV1ZqTW1abU5UZGhOVGdpZlE9PdJftz1h30OQfkp83LNkIm9ny/hOlGeFoh1cJLUlv/zW4KFoglvvVpiZrSIGYnasmysndbM5pe69MqNewSwdxD+vzRHNSGWR8SkCZTqp6LXu4CQrJcYnpR9iHtMJFg==";
        } else {
          licenseKey = process.env.REACT_APP_MB_KEY;
        }
        
        const loadedSdk = await createCaptureSdk({
          licenseKey,
          resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
        });

        // Disable blur detection for all desktop devices and specified sites
        if(settings.site_name === 'GAVS-PROD-TEST' || 
           settings.site_name === "GAVS-PROD-RO" || 
           !isMobile){
          await loadedSdk.updateAnalyzerSettings({
            blurPolicy: "disabled", // Disables blur detection
            glarePolicy: "disabled" // Also disable glare detection
          });
        }
        
        // For Veratad sites, optimize for quality with enhanced settings
        if(settings.site_name && settings.site_name.toLowerCase().includes('veratad')){
          await loadedSdk.updateAnalyzerSettings({
            captureStrategy: "optimize-for-quality",
            adjustMinimumDocumentDpi: true,
            minimumDocumentDpi: 400,
            keepDpiOnTransformedDocumentImage: true,
            lightingThresholds: {
              // Allow darker lighting; frames below this are too dark (0-1 scale)
              tooDarkThreshold: 0.1,
            }
          });
        }

        // For KID environments, allow darker ambient lighting before capture
        if (
          settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(
            settings.site_name.toLowerCase()
          )
        ) {
          await loadedSdk.updateAnalyzerSettings({
            lightingThresholds: {
              // Allow darker ambient lighting; frames below this are considered too dark
              tooDarkThreshold: 0.1,
            },
          });
        }

        setSdk(loadedSdk);
      } catch (error) {
        await sendResultToApi(settings.request_id, error.message);
        setErrorMessage("Failed to initialize the SDK: " + error.message);
      }
      setIsLoading(false);
    }

    preloadSdk();

    // Cleanup on component unmount
    return () => {
      captureComponentRef.current?.dismount();
    };
  }, [showFirefoxPrompt]);

  async function startCapture() {
    // prevent starting the capture process if one is already running
    if (isCapturingRef.current) {
      return;
    }
    isCapturingRef.current = true;
    setIsLoading(true);
    onStart();
    if (!sdk) {
      await sendResultToApi(
        settings.request_id,
        "SDK not initialized, please reload the page."
      );
      setErrorMessage("SDK not initialized, please reload the page.");
      setIsLoading(false);
      isCapturingRef.current = false;
      return;
    }
    await sendResultToApi(settings.request_id, "SDK loaded");

    try {
      const component = await createCaptureUiWithInstance(sdk, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);

            try {
              await sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }

            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          await sendResultToApi(settings.request_id, result.documentGroup);

          if (
            settings.journey.settings?.checkMbDocumentGroup &&
            result.documentGroup === "unknown"
          ) {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            return;
          }

          component.dismount();
          console.log('🚀 Microblink: Calling onUpload to show loading screen');
          onUpload();
          
          // Add delay to ensure loading animation starts
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Process front image
          await processAndUpload(
            result.firstCapture.imageResult.data,
            result.firstCapture.imageResult.width,
            result.firstCapture.imageResult.height,
            "front"
          );
          
          // Process back image if exists
          if (result.secondCapture?.imageResult) {
            await processAndUpload(
              result.secondCapture.imageResult.data,
              result.secondCapture.imageResult.width,
              result.secondCapture.imageResult.height,
              "back"
            );
          }
          
          // Keep loading animation visible for a moment to show "Finalizing..."
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          onComplete();
          setIsLoading(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      isCapturingRef.current = false;
    }
  }

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  return (
    <>
      {errorMessage && (
        <Alert
          icon={<IconExclamationCircle />}
          color="red"
          title={errorMessage}
        >
          {errorMessage !==
            "The document type is unknown. Please try again." && (
            <Text>{text.dcams.errors.permissions}</Text>
          )}
        </Alert>
      )}
      <Button
        size="lg"
        fullWidth
        onClick={startCapture}
        disabled={!sdk || isLoading}
        loading={!sdk || isLoading}
        loaderPosition="left"
      >
        {text.dcams.button}
      </Button>
    </>
  );
}